export type PaginatedList<T> = Partial<{
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}>;

// Sequential fallback for responses that don't report `count` (PaginatedList is a
// Partial<>, so this is a legitimate shape, not just a defensive branch).
async function depaginateRecursive<T>(
  paginatedApi: (page: number) => Promise<PaginatedList<T>>,
  previousResults: T[],
  pageNumber: number,
): Promise<T[]> {
  const page = await paginatedApi(pageNumber);
  const results = [...previousResults, ...(page.results ?? [])];
  if (page.next) {
    return depaginateRecursive(paginatedApi, results, pageNumber + 1);
  }
  return results;
}

// Fetches all pages of a paginated endpoint. When the first page reports `count`,
// the remaining pages are already known and fetched in parallel instead of one
// after another; falls back to the sequential walk when `count` is absent.
export async function depaginate<T>(paginatedApi: (page: number) => Promise<PaginatedList<T>>): Promise<T[]> {
  const firstPage = await paginatedApi(1);
  const firstPageResults = firstPage.results ?? [];

  if (!firstPage.next || firstPageResults.length === 0) {
    return firstPageResults;
  }

  if (typeof firstPage.count !== 'number') {
    return depaginateRecursive(paginatedApi, firstPageResults, 2);
  }

  const totalPages = Math.ceil(firstPage.count / firstPageResults.length);
  const remainingPages = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => paginatedApi(i + 2)));

  const results = [firstPageResults, ...remainingPages.map(page => page.results ?? [])].flat();

  // A row was inserted mid-flight and pushed a new page into existence beyond what
  // `count` predicted — walk the rest sequentially from there.
  const lastPage = remainingPages[remainingPages.length - 1];
  if (lastPage?.next) {
    const tail = await depaginateRecursive(paginatedApi, [], totalPages + 1);
    return [...results, ...tail];
  }

  return results;
}
